import React, { useState, useEffect } from "react";
import { Button, Modal } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { deletePatient, getPatients, uploadCsv } from "../../../apis/patients";
import { setPatientData } from "../../../redux/slices/patientSlice";
import { useDispatch, useSelector } from "react-redux";
import { toastify } from "../../../helperFunctions/toastify";
import { checkPermission } from "../../../helperFunctions/checkPermission";
// import CsvDownloadButton from "react-json-to-csv";
import Papa from "papaparse";
import moment from "moment";

import FilterComponent from "../../SharedComponents/FilterComponent";
import DeleteModal from "../../SharedComponents/DeleteModal";
import {
	customDateSort,
	customNumberSort,
	customStringSort,
} from "../../../helperFunctions/utils";

function Patient() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const userData = useSelector((state) => state.userData.value);

	const [patients, setPatients] = useState([]);
	const [upload, setUpload] = useState(false);
	const [file, setFile] = useState(null);
	const [filteredData, setFilteredData] = useState([]);
	const [show, setShow] = useState(false);
	const [deleting, setDeleting] = useState("");

	const getData = () => {
		let _data = [...patients].map((e) => {
			return {
				accountNumber: e.accountNumber,
				...e.patientProfile,
				emergencyContact: e.emergencyContact
					? `${e.emergencyContact.name || "Unknown Name"} - ${e.emergencyContact.relation || "Unknown Relation"
					} - ${e.emergencyContact.contactNo || "Unknown Contact"}`
					: "null",
				createdAt: e.created_at,
				isActive: e.isActive,
				isDeleted: e.isDeleted,
				updatedAt: e.updated_at,
			};
		});

		return _data;
	};

	const handleClose = () => {
		setShow(false);
		setDeleting("");
	};

	const populatePatients = async () => {
		const response = await getPatients();
		if (response.status !== "Fail") {
			if (Array.isArray(response)) {
				setPatients(response);
			}
		} else {
			if (response?.message === "No data Found") {
				setPatients([]);
			} else {
				toastify(response?.message, "error");
			}
		}
	};

	const handleUpload = async () => {
		if (file) {
			let formData = new FormData();
			formData.append("file", file);
			const response = await uploadCsv(formData);
			if (response?.status !== "Fail") {
				toastify("Patient(s) imported successfully!", "success");
				populatePatients();
			} else {
				console.log({ response });
				toastify(response?.message, "error");
			}
			setUpload(false);
		} else {
			setUpload(false);
		}
	};

	const addPatient = () => {
		dispatch(setPatientData());
		navigate("/add-patient");
	};

	const editPatient = (e) => {
		dispatch(setPatientData(e));
		navigate("/add-patient");
	};

	const removePatient = async () => {
		setShow(false);
		const response = await deletePatient(deleting);
		if (response) {
			populatePatients();
			toastify("Patient deleted successfully!", "success");
		} else {
			toastify(response?.message, "error");
		}
	};

	useEffect(() => {
		populatePatients();
	}, []);

	const columns = [
		{
			name: "No.",
			selector: (row) => row.no,
			wrap: true,
			width: "160px",
			sortable: true,
			sortFunction: (a, b) => customNumberSort(a, b, "index"),
		},

		{
			name: (
				<div className="d-flex align-items-center" id="ovly31">
					<span className="me-2">Account Number</span>
					{
						<FilterComponent
							type="search"
							array={patients}
							setArray={setFilteredData}
							value="AccountNumber"
						/>
					}
				</div>
			),
			selector: (row) => row.accountNumber,
			wrap: true,
			width: "200px",
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "accountNumberText"),
		},

		{
			name: (
				<div className="d-flex align-items-center" id="ovly32">
					<span className="me-2">Patient Name</span>
					{
						<FilterComponent
							type="search"
							array={patients}
							setArray={setFilteredData}
							value="PatientName"
						/>
					}
				</div>
			),
			selector: (row) => row.name,
			wrap: true,
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "nameText"),
		},
		{
			name: (
				<div className="d-flex align-items-center" id="ovly35">
					<span className="px-2">Contact No</span>
					{
						<FilterComponent
							type="search"
							array={patients}
							setArray={setFilteredData}
							value="ContactNo"
						/>
					}
				</div>
			),
			selector: (row) => row.contact,
			wrap: true,
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "nameText"),
		},

		{
			name: "Last Visit Date",
			selector: (row) => row.lastPayment ? moment(row.lastPayment).format("DD/MM/YYYY") : "-",
			width: "160px",
			sortable: true,
			// sortFunction: (a, b) => customDateSort(a, b, "accountNumber"),
		},

		{
			name: (
				<div className="d-flex align-items-center" id="ovly33">
					<span className="me-2">Gender</span>
					{
						<FilterComponent
							type="radio"
							array={patients}
							setArray={setFilteredData}
							options={["Male", "Female"]}
							value="Gender"
						/>
					}
				</div>
			),
			selector: (row) => row.gender,
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "gender"),
			width: "150px",
		},
		{
			name: "Age",
			selector: (row) =>
				row.dob
					? new Date().getFullYear() - new Date(row.dob).getFullYear()
					: "-",
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "dob"),
		},
		// {
		//   name: "DOB",
		//   selector: (row) =>
		//     row.dob && row.dob !== ""
		//       ? new Date(row.dob).toLocaleDateString()
		//       : "NaN",
		// },
		{
			name: (
				<div className="d-flex align-items-center" id="ovly34">
					<span className="me-2">Membership</span>
					{
						<FilterComponent
							type="radio"
							array={patients}
							setArray={setFilteredData}
							options={["True", "False"]}
							value="Active"
						/>
					}
				</div>
			),
			selector: (row) => (row.isMember === true ? "True" : "False"),
			width: "150px",
		},

		{
			name: "Status",
			selector: (row) => row.isActive,
			width: "150px",
		},

		{
			name: "Action",
			selector: (row) => row.action,
		},
	];

	const data = (filteredData.length === 0 ? patients : filteredData)?.map(
		(e, index) => {
			return {
				id: e._id,
				index: index,
				nameText: e?.patientProfile?.fullName,
				name: (
					<div
						className="cursor-pointer"
						onClick={() => editPatient(e)}
					>
						{e?.patientProfile?.fullName}
					</div>
				),
				contact: e?.patientProfile?.contactNo,
				gender: e?.patientProfile?.gender,
				dob: e?.patientProfile?.dob,
				isMember: e?.isMember,
				lastPayment: e?.lastPayment,
				isActive:
					e.isActive === true ? (
						<div className="active-status">Active</div>
					) : (
						<div className="inactive-status">In Active</div>
					),
				accountNumberText: e?.accountNumber,
				accountNumber: (
					<div
						className="cursor-pointer"
						onClick={() => editPatient(e)}
					>
						{e?.accountNumber}
					</div>
				),
				createdAt: e?.created_at,
				no: (
					<div
						className="cursor-pointer"
						onClick={() => editPatient(e)}
					>
						{index + 1}
					</div>
				),

				action: (
					<div className="d-flex">
						{checkPermission(userData, "Patient", "update") && (
							<div
								className="mx-3 cursor-pointer icon-width"
								onClick={() => {
									editPatient(e);
								}}
							>
								<i className="fa fa-pen"></i>
							</div>
						)}
						{checkPermission(userData, "Patient", "delete") && (
							<div
								className="mx-3 cursor-pointer icon-width"
								onClick={() => {
									setDeleting(e._id);
									setShow(true);
								}}
							>
								<i className="fa fa-trash"></i>
							</div>
						)}
					</div>
				),
			};
		},
	);

	const conditionalRowStyles = [
		{
			when: (row) => row,
			style: {
				borderWidth: "0px",
			},
		},
		{
			when: (row) => row.index % 2 === 1,
			style: {
				backgroundColor: "#f8f8f8",
				borderWidth: "0px",
			},
		},
		{
			when: (row) => {
				let days = Math.round(
					Math.abs(new Date() - new Date(row.createdAt)) /
					(1000 * 60 * 60 * 24),
				);
				if (days < 2) {
					return true;
				} else {
					return false;
				}
			},
			style: {
				backgroundColor: "#eaeaff",
				borderWidth: "0px",
			},
		},
	];

	const customStyles = {
		headCells: {
			style: {
				justifyContent: "center",
			},
		},
		cells: {
			style: {
				justifyContent: "center",
				textAlign: "center",
			},
		},
	};

	return (
		<div className="container p-0">
			<div className="mainHeading px-4">
				<div>
					<h1>Patient Database</h1>
					<p className="total-font">
						Total Result: {patients?.length}
					</p>
				</div>
				<DeleteModal
					content="Confirm Patient Deletion?"
					onYes={removePatient}
					onNo={handleClose}
					show={show}
				/>
				{checkPermission(userData, "Patient", "create") && (
					<div className="d-flex align-items-center">
						{!upload ? (
							<Button
								className="addItem-new-pat"
								onClick={() => setUpload(true)}
							>
								Upload Patients
							</Button>
						) : (
							<>
								<input
									type={"file"}
									accept={".csv"}
									onChange={(e) => {
										setFile(e.target.files[0]);
									}}
								/>
								<Button
									className="upload-button"
									onClick={handleUpload}
								>
									{file === null ? "Cancel" : "Upload"}
								</Button>
							</>
						)}

						<Button
							className="addItem-new-pat mx-4"
							onClick={() => {
								var csv = Papa.unparse(getData(), [
									{
										quotes: false,
										quoteChar: '"',
										escapeChar: '"',
										delimiter: ",",
										header: true,
										newline: "\r\n",
										skipEmptyLines: false,
										columns: null,
									},
								]);
								console.log({ csv });
								var blob = new Blob([csv], {
									type: "text/csv",
								});
								var url = URL.createObjectURL(blob);
								var pom = document.createElement("a");
								pom.href = url;
								pom.setAttribute("download", "patients.csv");
								pom.click();
							}}
						>
							Download Patients
						</Button>

						<Button
							className="addItem-new-pat"
							onClick={addPatient}
						>
							+ New Patient
						</Button>
					</div>
				)}
			</div>
			{checkPermission(userData, "Patient", "view") && (
				<DataTable
					columns={columns}
					data={data || []}
					pagination
					customStyles={customStyles}
					conditionalRowStyles={conditionalRowStyles}
					responsive="true"
				/>
			)}
		</div>
	);
}

export default Patient;
